import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, LockIcon } from 'lucide-react';
import { CTAButton } from './ui/CTAButton';
import { trackAddToCart } from '../lib/metaPixel';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function ProofSection() {
  return <section className="bg-white py-24 px-6" data-id="element-22">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeIn} className="max-w-3xl mx-auto text-center" data-id="element-23">
        <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-24">
          ¿Qué cambia en 7 días?
        </h2>
        <p className="text-xl text-brown leading-relaxed font-light" data-id="element-25">
          No garantizamos que tu hijo nunca más tenga un berrinche (eso es parte
          de su desarrollo), pero garantizamos que en una semana,{' '}
          <strong className="font-semibold text-brown-dark" data-id="element-26">
            tú ya no tendrás miedo de ellos
          </strong>
          . Sabrás exactamente qué hacer, los berrinches durarán la mitad del
          tiempo y la relación con tu hijo se sentirá más conectada que nunca.
        </p>
      </motion.div>
    </section>;
}
export function FAQSection() {
  const faqs = [{
    q: '¿Es necesario leer mucho?',
    a: 'No. Está diseñado para padres con poco tiempo. Puedes leerlo en una tarde y aplicarlo hoy mismo.'
  }, {
    q: '¿Funciona con niños rebeldes?',
    a: 'Sí, porque no tratamos la rebeldía, sino la desregulación biológica del cerebro infantil.'
  }, {
    q: '¿Si lo abrazo no lo estoy malcriando?',
    a: 'Al contrario. Le estás dando seguridad. La seguridad es la base de la independencia y la buena conducta.'
  }, {
    q: '¿Cómo recibo el producto?',
    a: 'Es un archivo PDF optimizado para ver en tu celular, tablet o computador.'
  }];
  return <section className="bg-cream-dark py-24 px-6" data-id="element-27">
      <div className="max-w-3xl mx-auto" data-id="element-28">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-29">
          Preguntas Frecuentes
        </motion.h2>

        <div className="space-y-4" data-id="element-30">
          {faqs.map((faq, idx) => <FAQItem key={idx} question={faq.q} answer={faq.a} data-id="element-31" />)}
        </div>
      </div>
    </section>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div initial="hidden" whileInView="visible" viewport={{
    once: true
  }} variants={fadeIn} className="bg-white rounded-2xl shadow-sm overflow-hidden" data-id="element-32">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" data-id="element-33">
        <span className="font-bold text-lg text-brown-dark" data-id="element-34">{question}</span>
        <ChevronDownIcon className={`w-5 h-5 text-teal-dark transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-35" />
      </button>
      <AnimatePresence data-id="element-36">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-37">
            <div className="px-6 pb-5 text-brown leading-relaxed border-t border-cream pt-4" data-id="element-38">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function OfferSection() {
  return <section id="oferta" className="bg-white py-24 px-6" data-id="element-39">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeIn} className="max-w-4xl mx-auto bg-cream rounded-3xl p-8 md:p-16 text-center shadow-soft border border-cream-dark relative overflow-hidden" data-id="element-40">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-41"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-42"></div>

        <div className="relative z-10" data-id="element-43">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-44">
            Accede hoy al Kit "Berrinches en Calma"
          </h2>

          <div className="my-10" data-id="element-45">
            <span className="inline-block bg-teal-dark text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5" data-id="element-46">
              62% de descuento
            </span>
            <div className="flex items-center justify-center gap-4 mb-2" data-id="element-47">
              <span className="text-2xl md:text-3xl text-brown/40 line-through font-serif" data-id="element-48">
                $17 USD
              </span>
            </div>
            <div className="text-6xl md:text-7xl font-bold text-teal-dark font-serif" data-id="element-49">
              $6.39 <span className="text-2xl text-brown font-sans" data-id="element-50">USD</span>
            </div>
          </div>

          <p className="text-lg text-brown mb-10 max-w-xl mx-auto italic" data-id="element-51">
            (Menos de lo que cuesta un café, para una solución que te servirá
            por años).
          </p>

          <CTAButton href="https://pequeguia.lat/cart/42271899451495:1" onClick={e => {
          e.preventDefault();
          trackAddToCart('https://pequeguia.lat/cart/42271899451495:1');
        }} className="w-full md:w-auto text-sm md:text-lg px-10 py-5 mb-6" data-id="element-52">
            ¡SÍ, QUIERO CRIAR CON CALMA Y SIN CULPA!
          </CTAButton>

          <div className="flex items-center justify-center text-brown text-sm mt-6 space-x-2" data-id="element-53">
            <LockIcon className="w-4 h-4 text-teal-dark" data-id="element-54" />
            <span data-id="element-55">Pago seguro y descarga instantánea.</span>
          </div>
        </div>
      </motion.div>
    </section>;
}